import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody, CardFooter } from '../components/Card.js'
import { Section } from '../components/Section.js'
import { Button } from '../components/Button.js'
import { Field, Input, Select, Textarea } from '../components/Field.js'
import { Icon } from '../components/Icon.js'
import { FeatureGate } from '../components/FeatureGate.js'
import { FEATURES } from '@academy/config'

export const Route = createFileRoute('/_app/communicate')({
  component: () => (
    <FeatureGate feature={FEATURES.PARENT_COMMUNICATION}>
      <CommunicatePage />
    </FeatureGate>
  ),
})

interface AcademyResponse {
  features: Record<string, boolean>
}

interface ClassRow {
  id: string
  name: string
  isActive: boolean
}

interface BroadcastResult {
  sent: number
  skipped: number
  total: number
  scope: 'all' | 'class'
}

function CommunicatePage() {
  const academy = useQuery({
    queryKey: ['academy'],
    queryFn: async () => {
      const res = await api<AcademyResponse>('/api/v1/academy')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })
  const classes = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [scope, setScope] = useState<'all' | 'class'>('all')
  const [classId, setClassId] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<BroadcastResult | null>(null)

  const broadcast = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        subject,
        message,
        scope,
      }
      if (scope === 'class' && classId) payload.classId = classId
      const res = await api<BroadcastResult>('/api/v1/notifications/broadcast', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (data) => {
      setResult(data)
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Send failed'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!subject.trim() || !message.trim()) {
      setError('Both subject and message are required')
      return
    }
    if (scope === 'class' && !classId) {
      setError('Pick a class to scope the broadcast')
      return
    }
    broadcast.mutate()
  }

  const enabled = academy.data?.features?.parent_communication

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="Communicate"
        description="Send WhatsApp + email announcements to parents at your academy."
      />

      {!academy.isLoading && !enabled ? (
        <div className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-tint px-4 py-3 text-sm text-warning">
          <Icon name="alert" size={16} className="mt-0.5" />
          <p>
            Parent broadcasts are not included on your current plan. Ask your platform
            admin to enable the <strong>parent_communication</strong> feature.
          </p>
        </div>
      ) : null}

      <Section title="New broadcast">
        <Card>
          <form onSubmit={onSubmit}>
            <CardBody className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Audience" required>
                  <Select
                    value={scope}
                    onChange={(e) => setScope(e.target.value as 'all' | 'class')}
                  >
                    <option value="all">All parents</option>
                    <option value="class">A specific class</option>
                  </Select>
                </Field>
                {scope === 'class' ? (
                  <Field label="Class" required>
                    <Select value={classId} onChange={(e) => setClassId(e.target.value)}>
                      <option value="">Pick a class</option>
                      {classes.data
                        ?.filter((c) => c.isActive)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </Select>
                  </Field>
                ) : null}
              </div>
              <Field label="Subject" required hint="Shown in the email subject and used as the headline.">
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Holiday on Friday"
                  required
                  maxLength={120}
                />
              </Field>
              <Field label="Message" required hint="Plain text. Newlines preserved.">
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  required
                  maxLength={2000}
                />
              </Field>
            </CardBody>
            <CardFooter className="flex items-center justify-between">
              <p className="text-xs text-fg-subtle">
                {result ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <Icon name="check" size={12} />
                    Sent to {result.sent}
                    {result.skipped > 0 ? ` · ${result.skipped} skipped` : ''}
                  </span>
                ) : (
                  'Each parent gets one message even if they have multiple children that match.'
                )}
                {error ? <span className="ml-2 text-danger">{error}</span> : null}
              </p>
              <Button
                type="submit"
                loading={broadcast.isPending}
                disabled={!enabled}
              >
                Send broadcast
              </Button>
            </CardFooter>
          </form>
        </Card>
      </Section>
    </div>
  )
}
